/**
 * The estimate as the customer reads it, on their phone.
 *
 * This renders a `PresentedDocument` value and nothing else. It does no
 * arithmetic beyond adding up what the customer has ticked, reads no job, and
 * knows nothing about Supabase — everything on screen was decided by
 * `buildPresentedDocument` when the rep pressed Present, and frozen into the
 * snapshot at that moment. That is what makes the copy in the customer's hands
 * stable while the office carries on working.
 *
 * One column, one scroll, big type and generous tap targets. Most of the people
 * who open this are standing in a driveway.
 *
 * ## The 2026-08-17 rebuild
 *
 * Drawn to the design Jonathon worked up with Fable: cover, the photographs of
 * their roof, how we build it, a section per package, the upgrades once,
 * the summary where they choose and see what is due, financing, the team,
 * the colours, and the agreement.
 *
 * Two things this file deliberately does not do. It does not decide what is on
 * the document — a section the rep turned off is simply not in the value, so
 * there is no hidden state here to get wrong. And it does not hold the money
 * rules: `presentedTotal` and `presentedDeposit` are the same functions the
 * server checks a signature against, so the number under the customer's thumb
 * and the number on the acceptance cannot drift apart.
 */

/*
 * The company's mark, not Roof Board's.
 *
 * This page is the one thing in the app a customer reads, so the logo on it is
 * CopperRidge's. Roof Board is our internal tool and a brand nobody outside the
 * office has any reason to have heard of.
 */
import { COMPANY_LOGO_URL } from "../../logo";
import { ColorViewer } from "./ColorViewer";
import { ProductShowcaseView } from "./ProductShowcase";
import { ProductVideoPlayer } from "./ProductVideoPlayer";
import { UpgradeVideoPlayer } from "./UpgradeVideo";
import {
  CLASS_4_INSURANCE_NOTE,
  documentChoosesMany,
  documentPackages,
  financingPayment,
  financingProgramPayment,
  financingProgramTerms,
  formatApr,
  formatMoney,
  formatTerm,
  includedUpgradeKeys,
  localDateString,
  nextSigningStep,
  presentedDeposit,
  presentedSchedule,
  presentedTotal,
  type PresentedScheduleRow,
  signatureIsComplete,
  signedDateIsValid,
  signedEstimateIds,
  signedDateWindow,
  signingSteps,
  toggleChoice,
  upgradeIsIncluded,
  type Cents,
  type ContractBlock,
  type Deposit,
  type DocumentSignature,
  type DocumentSection,
  type PresentedDocument,
  type PresentedPackage,
  type PresentedUpgrade,
} from "@roof-hawk/core";
import { Fragment, useEffect, useState } from "react";

export interface SignaturePayload {
  signerName: string;
  signerEmail: string | null;
  signerPhone: string | null;
  signedDate: string;
  consented: boolean;
  initials: Record<string, string>;
  /**
   * Which package they bought. The server checks it against the options the
   * proposal actually offered — this is the customer naming their pick, not
   * setting it.
   *
   * The first of `chosenEstimateIds`, and sent alongside it so that a request
   * from this page is one an older server would still understand.
   */
  chosenEstimateId: string | null;
  /**
   * Everything they bought, where the document let them buy more than one
   * (ADR 0120). One id on every ordinary document, because the page will not
   * let a second be picked there.
   */
  chosenEstimateIds: string[];
  /**
   * Which upgrades they ticked, by key. Includes the ones their package came
   * with — the server prices them at nothing, so saying so costs nothing and
   * the record of what they signed for is complete.
   */
  chosenUpgrades: string[];
}

/**
 * The small grey line above each heading.
 *
 * They turn a long scroll into a document with a shape, so somebody halfway
 * down knows whether they are still being sold to or have reached the part that
 * costs money. Each one has to say something its heading does not — an eyebrow
 * that repeats its own heading is two lines of nothing.
 */
/*
 * One word for signing, and it belongs to the button that signs.
 *
 * Four controls used to say it — this eyebrow, the Next button below, the
 * sticky bar's "Review & sign", and the real submit — and three of them only
 * scrolled the page to the Summary. To a customer holding the phone that is
 * four things called the same thing, three of which do not do it. The three
 * that move the page now say where they are going; "sign" is left to the one
 * control that actually signs.
 */
const EYEBROW: Partial<Record<DocumentSection["id"], string>> = {
  findings: "Your inspection",
  scope: "How your roof goes on",
  packages: "Your options",
  compare: "Compare & choose",
  summary: "What it comes to",
  financing: "Flexible options",
  colors: "Tap a package to explore",
  work: "Roofs we have finished",
  contract: "The agreement",
};

const CONTRACT_ANCHOR = "pdoc-contract";
const SUMMARY_ANCHOR = "pdoc-summary";

/** Where each section sits, so the Next buttons have something to aim at. */
function sectionAnchor(id: DocumentSection["id"]): string {
  if (id === "summary") return SUMMARY_ANCHOR;
  if (id === "contract") return CONTRACT_ANCHOR;
  return `pdoc-${id}`;
}

/**
 * What the button at the foot of a section calls the one it goes to.
 *
 * Keyed by where it lands, never by where it starts. A document holds a subset
 * of the sections — a repair has no inspection photographs and no eight-step
 * page — so "the section after the cover" is not a fixed thing, and a label
 * chosen by what you are leaving promises whatever came next last time.
 *
 * Short, and in the customer's terms rather than the document's headings:
 * "Your options" reads as somewhere to go, "Your Options" as a title.
 */
const NEXT_LABEL: Partial<Record<DocumentSection["id"], string>> = {
  findings: "Your roof photos",
  scope: "How your roof goes on",
  packages: "Your options",
  compare: "Compare your options",
  showcase: "Gutter guards",
  upgrades: "Optional extras",
  summary: "Your price",
  financing: "Financing",
  colors: "Color options",
  work: "See our work",
  contract: "The agreement",
};

/**
 * What the button says, for the section it lands on.
 *
 * "Your options" is a promise of a choice, and a document quoting one price
 * offers none — a chimney repair that says "Next: Your options" and then shows
 * one number reads as a page that lost something (Samuel, 2026-08-21). So a
 * sole package is announced by its own name, which is what the heading down
 * there actually says.
 */
function nextLabel(
  section: DocumentSection,
  packages: readonly PresentedPackage[],
): string {
  if (section.id === "packages" && packages.length === 1) {
    return packages[0]?.name ?? section.heading;
  }
  return NEXT_LABEL[section.id] ?? section.heading;
}

/**
 * Move down the page without touching the address bar.
 *
 * Never an `href="#…"`. The office app routes on the hash, so an anchor link
 * inside a preview was read as a route change: the rep tapped "Read the
 * agreement" and landed on the dashboard with the estimate gone (Jonathon,
 * 2026-08-19). Scrolling is what these controls always meant anyway.
 */
function jumpTo(id: string) {
  const target = window.document.getElementById(id);
  target?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * The way on to the next part, in copper, at the foot of every section.
 *
 * Jonathon, 2026-08-19: "there needs to be copper arrows showing the customer
 * what to do. think as simple as possible. even next buttons." A proposal is
 * one long scroll and the customer is holding a phone; this is the thing that
 * says the page continues and what is coming.
 */
function NextStep({ to, label }: { to: string; label: string }) {
  return (
    <div className="pdoc-next">
      <button type="button" onClick={() => jumpTo(to)}>
        <span>Next: {label}</span>
        <span className="pdoc-next-arrow" aria-hidden>
          ↓
        </span>
      </button>
    </div>
  );
}

/**
 * Where a colour photograph lives.
 *
 * `hdz-slate-2` → `/shingle-colors/hdz-slate-2.jpg`. Built rather than looked
 * up — there are over a hundred of them and a table of a hundred one-line
 * entries is a table nobody would keep in step. The pattern check is what a map
 * gave for free: a key from somewhere unexpected draws nothing rather than
 * asking the customer's phone for a file that is not there.
 */
function colorPhotoUrl(folder: string, key: string): string | null {
  return /^[a-z]+(-[a-z]+)*-\d+$/.test(key) ? `/${folder}/${key}.jpg` : null;
}

export function PresentedDocumentView({
  document,
  companySignedAt,
  onSign,
  signing,
  error,
  preview,
  record,
}: {
  document: PresentedDocument;
  companySignedAt: string | null;
  onSign: (signature: SignaturePayload) => void | Promise<void>;
  signing?: boolean;
  error?: string | null;
  /**
   * The rep looking at it before it is sent, rather than the customer.
   *
   * Everything reads and behaves the same — packages can be picked, upgrades
   * ticked, the total follows — because a preview that behaved differently
   * would be a preview of a different document. The one thing switched off is
   * the signature: this document does not exist yet, there is no proposal for
   * an acceptance to belong to, and a rep tapping Sign in a preview must not be
   * able to produce one.
   */
  preview?: boolean;
  /**
   * The office reading a document that has already gone out.
   *
   * The same document, drawn the same way, with the customer's own answer
   * locked into it: the package they picked is picked, the upgrades they took
   * are ticked, and neither can be changed by whoever is looking. In place of
   * the signing form it carries the signature — who signed, on what date, from
   * where — because on this screen that is the question being asked.
   *
   * `record` with a null `signature` is a document nobody has signed yet, read
   * back from the office. It shows what was offered and says so; it still
   * cannot be signed here, because a signature is the customer's to give.
   */
  record?: {
    signature: DocumentSignature | null;
    initials?: Record<string, unknown>;
  } | null;
}) {
  const packages = documentPackages(document);
  /*
   * Which package they have picked. Null until they pick one, deliberately.
   *
   * The recommended package is *marked*, not pre-selected. Pre-selecting it
   * would mean somebody could scroll past the choice entirely and sign a roof
   * they never chose — and this document is an agreement, so which roof they
   * bought has to be an act rather than a default they failed to notice.
   *
   * A document offering one package is not a choice, so that one is the answer
   * from the start.
   */
  /*
   * Whether this document is a choice between packages or a list of things to
   * buy. Frozen into the document when it was sent (ADR 0120), so a link that
   * went out under one rule keeps it however the job has been edited since.
   */
  const chooseMany = documentChoosesMany(document);
  const [chosen, setChosen] = useState<string[]>(() => {
    if (record?.signature) return [...signedEstimateIds(record.signature)];
    return packages.length === 1 && packages[0] ? [packages[0].estimateId] : [];
  });
  /*
   * Ticked upgrades, by key — only the ones the customer ticked themselves.
   *
   * What their package already covers is *not* kept here. Those are worked out
   * from the chosen package every render, so switching from Bulletproof (which
   * includes the high temperature ice and water) to Bare Bones (which does not)
   * cannot leave a stale free tick behind. `includedUpgradeKeys` owns that rule.
   */
  const [ticked, setTicked] = useState<string[]>([
    ...(record?.signature?.upgradeKeys ?? []),
  ]);
  /*
   * A signed document is a record, so nothing on it moves.
   *
   * Not a cosmetic lock: the whole value of this screen is that it shows what
   * was agreed, and a package somebody can re-pick while reading it is a screen
   * that can be made to say the wrong thing about a contract.
   */
  const locked = Boolean(record?.signature);
  const included = includedUpgradeKeys(document, chosen);
  const selected = [...new Set([...included, ...ticked])];
  const total = presentedTotal(document, chosen, selected);
  const deposit = presentedDeposit(document, chosen, selected);
  const schedule = presentedSchedule(document, chosen, selected);
  const needsAChoice = packages.length > 1 && chosen.length === 0;

  /*
   * Ticking one, on either kind of document.
   *
   * A choice un-picks the last answer; a list of things to buy does not. The
   * rule sits here rather than in the two components that call it, so the
   * Choose buttons up the page and the rows in the summary cannot end up
   * behaving differently on the same document.
   */
  const onChoose = (estimateId: string) =>
    setChosen((was) => toggleChoice(document, was, estimateId));
  /*
   * The cover, for the two boxes the signing form should not be asking for.
   *
   * Jonathon, 2026-08-19: "it should autofill the customer's email and phone
   * number based on what's already in roofboard." It is on the document
   * already — the cover prints it — so the form starts with it rather than
   * making somebody type what we sent them.
   */
  const cover = document.sections.find(
    (section): section is Extract<DocumentSection, { id: "cover" }> =>
      section.id === "cover",
  );
  /** The upgrades as offered, so the summary can name the ones they ticked. */
  const upgrades =
    document.sections.find(
      (section): section is Extract<DocumentSection, { id: "upgrades" }> =>
        section.id === "upgrades",
    )?.upgrades ?? [];

  const onTick = (key: string) =>
    setTicked((was) =>
      was.includes(key) ? was.filter((k) => k !== key) : [...was, key],
    );

  return (
    <div className="pdoc">
      {document.sections.map((section, index) => {
        const next = document.sections[index + 1];
        return (
          <Fragment key={section.id}>
            {section.id === "contract" ? (
              <ContractSectionView
                section={section}
                companySignedAt={companySignedAt}
                chosen={chosen}
                chosenUpgrades={selected}
                needsAChoice={packages.length > 1}
                chooseMany={chooseMany}
                customerEmail={cover?.customerEmail ?? null}
                customerPhone={cover?.customerPhone ?? null}
                onSign={onSign}
                signing={signing}
                error={error}
                preview={preview}
                record={record}
              />
            ) : (
              <Section
                section={section}
                packages={packages}
                upgrades={upgrades}
                chosen={chosen}
                chooseMany={chooseMany}
                onChoose={locked ? () => undefined : onChoose}
                ticked={selected}
                onTick={locked ? () => undefined : onTick}
                total={total}
                deposit={deposit}
                schedule={schedule}
              />
            )}
            {next ? (
              <NextStep
                to={sectionAnchor(next.id)}
                label={nextLabel(next, packages)}
              />
            ) : null}
          </Fragment>
        );
      })}

      <footer className="pdoc-foot">
        <img className="pdoc-foot-logo" src={COMPANY_LOGO_URL} alt="" />
        <p className="pdoc-rev">
          Revision {document.revision} · {document.date}
        </p>
      </footer>

      {/* No bar on a record. It exists to walk a customer down to the
          signature, and there is nothing left to sign. */}
      {record ? null : (
        <SignBar
          totalFormatted={total?.totalFormatted ?? null}
          needsAChoice={needsAChoice}
          chooseMany={chooseMany}
          preview={preview}
        />
      )}
    </div>
  );
}

/**
 * The bar across the bottom of the phone: what it costs, and the way to the
 * signature.
 *
 * It exists because the summary is a long way down a single scroll. It takes
 * itself off screen once the agreement is in view — a fixed bar over the
 * signing form would cover the very button it is pointing at.
 */
function SignBar({
  totalFormatted,
  needsAChoice,
  chooseMany,
  preview,
}: {
  totalFormatted: string | null;
  needsAChoice: boolean;
  /** Several can be bought, so nothing here calls it "your package". */
  chooseMany?: boolean;
  /** In a preview the bar is not pinned — it is a page inside a modal. */
  preview?: boolean | undefined;
}) {
  const [atContract, setAtContract] = useState(false);

  useEffect(() => {
    const target = window.document.getElementById(CONTRACT_ANCHOR);
    if (!target || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setAtContract(Boolean(entry?.isIntersecting)),
      { rootMargin: "0px 0px -35% 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  if (atContract) return null;

  return (
    <div className={`pdoc-bar ${preview ? "inline" : ""}`}>
      <span className="pdoc-bar-total">
        {needsAChoice
          ? chooseMany
            ? "Nothing selected yet"
            : "No package selected yet"
          : totalFormatted
            ? `Total ${totalFormatted}`
            : chooseMany
              ? "Pick what you want"
              : "Pick a package"}
      </span>
      {/* This scrolls; it does not sign. Named for where it goes. */}
      <button
        type="button"
        className="pdoc-bar-go"
        onClick={() => jumpTo(SUMMARY_ANCHOR)}
      >
        {needsAChoice
          ? chooseMany
            ? "Choose your options"
            : "Choose your option"
          : "See your price"}
      </button>
    </div>
  );
}

function Section({
  section,
  packages,
  upgrades,
  chosen,
  chooseMany,
  onChoose,
  ticked,
  onTick,
  total,
  deposit,
  schedule,
}: {
  section: DocumentSection;
  packages: PresentedPackage[];
  /** Every upgrade offered, so the summary can name the ticked ones. */
  upgrades: PresentedUpgrade[];
  /** Everything picked so far. One id, unless the document allows several. */
  chosen: string[];
  /** Whether the customer may buy more than one of these (ADR 0120). */
  chooseMany: boolean;
  onChoose: (estimateId: string) => void;
  ticked: string[];
  onTick: (key: string) => void;
  total: { total: number; totalFormatted: string } | null;
  deposit: Pick<
    Deposit,
    "required" | "amountFormatted" | "text" | "atSigning"
  > | null;
  /** The rep's own schedule, laid out for what they chose, or null (ADR 0267). */
  schedule: PresentedScheduleRow[] | null;
}) {
  switch (section.id) {
    case "cover":
      return (
        <section className="pdoc-cover" id={sectionAnchor("cover")}>
          {section.coverPhotoUrl ? (
            <div className="pdoc-hero">
              <img src={section.coverPhotoUrl} alt={section.propertyAddress} />
            </div>
          ) : null}
          <div className="wrap">
            <div>
              {/* No date and no licence number. The cover says "Proposal" and
                  nothing else: the date is still on the document — the company
                  signature carries it — and the licence stays off the customer's
                  copy the same way it is off the contract (Jonathon,
                  2026-08-19). */}
              <h1>{section.heading}</h1>
            </div>
            <div className="to">
              <p>{section.customerName}</p>
              {section.customerEmail ? <p>{section.customerEmail}</p> : null}
              {section.customerPhone ? <p>{section.customerPhone}</p> : null}
              <p>{section.propertyAddress}</p>
            </div>
            <div className="from">
              <div>
                {section.repName ? <p>{section.repName}</p> : null}
                <p>{section.companyName}</p>
                {section.companyPhone ? <p>{section.companyPhone}</p> : null}
                {section.repEmail ? <p>{section.repEmail}</p> : null}
                {/* No licence number, on a document frozen today or two weeks
                    ago. Jonathon, 2026-08-21: it does not show anywhere. */}
              </div>
              <img src={COMPANY_LOGO_URL} alt={section.companyName} />
            </div>
          </div>
        </section>
      );

    case "findings":
      return (
        <Sec section={section} className="slate">
          {/* Large, and with nothing written under them. The caption the rep
              typed is still on the photograph for a screen reader; printed
              under each one it read as a list of faults rather than a look at
              the roof (Jonathon, 2026-08-17). */}
          <div className="pdoc-gallery">
            {section.photos.map((photo, index) => (
              <img
                key={index}
                src={photo.url}
                alt={photo.caption ?? ""}
                loading="lazy"
              />
            ))}
          </div>
        </Sec>
      );

    case "videos":
      /*
       * Directly under the photographs. The photographs argue there is a
       * problem; these show what we do about it, on repairs a homeowner has
       * never watched anybody carry out (Jonathon, 2026-09-04).
       */
      return (
        <Sec section={section}>
          <div className="pdoc-videos">
            {section.videos.map((video) => (
              <ProductVideoPlayer key={video.url} video={video} />
            ))}
          </div>
        </Sec>
      );

    case "scope":
      /*
       * Eight cards, the photograph swapping sides step to step — the packet
       * Jonathon wrote does that, and it is what stops eight near-identical
       * cards reading as a wall.
       */
      return (
        <Sec section={section} dark className="pdoc-scope" corner={false}>
          {section.steps.map((step, index) => (
            <article
              key={step.image}
              className={`pdoc-step ${index % 2 ? "slate flip" : ""}`}
            >
              <p className="bar">
                <small>Step {index + 1}</small>
                {step.heading}
              </p>
              <div className="body">
                <ul>
                  {step.points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
                <img
                  src={`/scope-of-work/${step.image}.jpg`}
                  alt=""
                  loading="lazy"
                />
              </div>
            </article>
          ))}
          <p className="pdoc-banner">{section.closer.bar}</p>
          <p className="pdoc-banner2">{section.closer.line}</p>
        </Sec>
      );

    case "packages":
      return (
        <>
          {section.packages.map((pack, index) => (
            <PackageSection
              key={pack.estimateId}
              pack={pack}
              anchorId={index === 0 ? sectionAnchor("packages") : undefined}
              chosen={chosen.includes(pack.estimateId)}
              // One package is not a choice, so it is not offered as one.
              // Jonathon, 2026-08-19: "if there's only one estimate presented
              // it shouldn't say chosen."
              soleOption={section.packages.length === 1}
              chooseMany={chooseMany}
              onChoose={() => onChoose(pack.estimateId)}
            />
          ))}
        </>
      );

    case "compare":
      // The roofs side by side, before the upgrades (Jonathon, 2026-10-07).
      // See `CompareSection` in packages/core and ADR 0274.
      return <CompareChart section={section} chosen={chosen} />;

    case "showcase":
      // What a gutter guard looks like, before the box that adds one
      // (Jonathon, 2026-10-01). See `ShowcaseSection` in packages/core.
      return <ProductShowcaseView section={section} />;

    case "upgrades":
      return (
        <Sec section={section} className="slate">
          <p className="pdoc-lead">
            {chooseMany
              ? "Add any of these to what you choose. Anything already included is marked."
              : "Add any of these to the package you choose. Anything already included in your package is marked."}
          </p>
          {section.upgrades.map((upgrade) => (
            <UpgradeRow
              key={upgrade.key}
              upgrade={upgrade}
              included={upgradeIsIncluded(upgrade, chosen)}
              ticked={ticked.includes(upgrade.key)}
              onTick={() => onTick(upgrade.key)}
            />
          ))}
        </Sec>
      );

    case "summary":
      return (
        <SummarySection
          section={section}
          packages={packages}
          upgrades={upgrades}
          chosen={chosen}
          chooseMany={chooseMany}
          onChoose={onChoose}
          ticked={ticked}
          total={total}
          deposit={deposit}
          schedule={schedule}
        />
      );

    case "financing":
      return (
        <Sec section={section} dark heading={twoLine(section.heading)}>
          {/* On top of the card, which is where Jonathon put it on
              2026-09-22. It answers the only question this page exists for,
              and it answers it with the customer's own hands (ADR 0202). */}
          {section.calculator ? (
            <FinancingCalculator
              calculator={section.calculator}
              total={total ? (total.total as Cents) : null}
            />
          ) : null}
          {section.columns.map((column) => (
            <div key={column.title} className="pdoc-box">
              <h3>{column.title}</h3>
              <ul>
                {column.lines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          ))}
          {/* The terms this job is quoted on, above the panels that sell
              financing in general. Absent unless somebody typed a rate and a
              term on the estimate (ADR 0201) — and left out entirely when the
              calculator is on the page, which says all of it and lets them
              move it. Two payment panels is two answers to one question. */}
          {section.quote && !section.calculator ? (
            <div className="pdoc-quote">
              <b>{section.quote.heading}</b>
              {section.quote.monthly ? (
                <strong className="pdoc-quote-amt">
                  {section.quote.monthly}
                </strong>
              ) : null}
              <ul>
                {section.quote.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <span>{section.quote.note}</span>
            </div>
          ) : null}
          <div className="pdoc-pill">
            <b>{section.pill.title}</b>
            <span>{section.pill.note}</span>
          </div>
          {section.apply ? (
            <div className="pdoc-qr">
              <b>{section.apply.title}</b>
              <span>{section.apply.note}</span>
              {/* A real link, not a button that calls something: this page is
                  a frozen snapshot and the customer may be reading it on a
                  phone with nothing else of ours open. */}
              <a
                className="pdoc-apply"
                href={section.apply.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                {section.apply.title}
              </a>
            </div>
          ) : section.qr ? (
            /* A document frozen before 2026-09-21 carries the QR slot this
               replaced, and has to go on reading as it was signed (ADR 0032). */
            <div className="pdoc-qr">
              <b>{section.qr.title}</b>
              <span>{section.qr.note}</span>
            </div>
          ) : null}
          <p className="pdoc-fine">{section.disclaimers.join(" · ")}</p>
        </Sec>
      );

    case "colors":
      return <ColorsSectionView section={section} />;

    case "work":
      /*
       * One page, one thing to tap. The videos are on our own channel and this
       * sends them there — see `WorkSection` in packages/core for why it is a
       * link rather than an embed, and why a commercial proposal has no such
       * page at all.
       */
      return (
        <Sec section={section} dark heading={twoLine(section.heading)}>
          <p className="pdoc-lead">{section.lead}</p>
          <div className="pdoc-work">
            <b>{section.link.title}</b>
            <span>{section.link.note}</span>
            {/* A real link, opened in its own tab. This page is a frozen
                snapshot the customer may be part-way through signing, and
                sending them to YouTube in the same tab loses whatever they
                have already ticked. */}
            <a
              className="pdoc-apply"
              href={section.link.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              {section.link.title}
            </a>
          </div>
        </Sec>
      );

    // The agreement is drawn by ContractSectionView in the document loop,
    // with the signing state this component does not have. Named so that
    // switch-exhaustiveness-check fails on a new section id until it gets a
    // case; the default stays for a frozen document carrying an id this
    // build no longer knows.
    case "contract":
    default:
      return null;
  }
}

/**
 * The payment calculator, on top of the financing card.
 *
 * Jonathon, 2026-09-22: "make a payment slider that the customer can interact
 * with on the presentation page when financing is selected … term and rate
 * varies by credit and is just an estimate."
 *
 * It is the one part of a frozen document the customer changes. Nothing it
 * does is written down or sent anywhere: this is a person working out whether
 * they can afford a roof, on their own phone, and the answer moving as they
 * drag is the whole point. The price they sign for is untouched by it, which
 * the lead line says out loud so nobody drags the slider expecting the roof to
 * get cheaper.
 */
/**
 * A down payment as the customer types it, in cents.
 *
 * Forgiving on the way in — a dollar sign, commas, spaces — because this is
 * somebody on a phone in their own kitchen, not a rep filling in a form. What
 * it will not do is invent a number: an empty or half-typed "$" is zero;
 * invalid text leaves the payment hidden until they correct it.
 */
function parseDownPayment(raw: string): Cents | null {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (cleaned === "") return 0 as Cents;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  const amount = Math.round(parsed * 100);
  return Number.isSafeInteger(amount) ? (amount as Cents) : null;
}

function FinancingCalculator({
  calculator,
  total,
}: {
  calculator: NonNullable<
    Extract<DocumentSection, { id: "financing" }>["calculator"]
  >;
  /** What they have chosen so far, or null while they have chosen nothing. */
  total: Cents | null;
}) {
  const [programKey, setProgramKey] = useState(calculator.defaultProgramKey);
  const program =
    calculator.programs.find((candidate) => candidate.key === programKey) ??
    calculator.programs[0]!;
  const terms = financingProgramTerms(program);
  const [months, setMonths] = useState<number | null>(null);
  // A term the current programme does not write is a term left over from the
  // programme before it, so each programme falls back to its own default
  // rather than carrying 144 months onto a twelve-month promotion.
  const chosenMonths =
    months != null && terms.includes(months)
      ? months
      : program.key === calculator.defaultProgramKey
        ? (calculator.defaultMonths ?? program.defaultMonths)
        : program.defaultMonths;

  /*
   * What they put down, theirs to change.
   *
   * Jonathon, 2026-09-22: "let the customer enter in a downpayment." The box
   * starts at whatever the rep put on the estimate and null means untouched,
   * so it follows the rep's figure until somebody types over it — and an
   * emptied box is $0, not "back to the rep's", because a customer who cleared
   * it meant nothing down.
   */
  const [downDraft, setDownDraft] = useState<string | null>(null);
  const typedDown = downDraft == null ? null : parseDownPayment(downDraft);
  const invalidDown = downDraft != null && typedDown == null;
  const downPayment = Math.min(
    Math.max(downDraft == null ? calculator.downPayment : (typedDown ?? 0), 0),
    total ?? Number.MAX_SAFE_INTEGER,
  ) as Cents;

  const payment =
    total == null || invalidDown
      ? null
      : financingProgramPayment(total, program, chosenMonths, downPayment);

  const tooSmall = total != null && total < calculator.minTotal;
  const tooLarge = total != null && total - downPayment > calculator.maxTotal;
  // A down payment big enough to leave under the floor is the same refusal for
  // a different reason, and it is one the customer can undo — so it says which
  // of the two happened rather than going blank.
  const overPaid =
    total != null && !tooSmall && total - downPayment < calculator.minTotal;
  const floor = formatMoney(calculator.minTotal).replace(/\.00$/, "");
  const ceiling = formatMoney(calculator.maxTotal).replace(/\.00$/, "");

  return (
    <div className="pdoc-calc">
      <b>{calculator.heading}</b>
      <span className="pdoc-calc-lead">{calculator.lead}</span>

      {/* A segmented control of pressed buttons, not a radiogroup. The one
          radio choice on a customer's copy is which package they are buying,
          and a screen reader should not hear a second one the same way — this
          picks how to illustrate a payment and changes nothing they sign.
          One plan is the rep's own quoted terms, with nothing to choose
          between, so there is no control to press (ADR 0250). */}
      {calculator.programs.length > 1 ? (
        <div className="pdoc-calc-plans" aria-label="Choose a payment plan">
          {calculator.programs.map((candidate) => (
            <button
              key={candidate.key}
              type="button"
              aria-pressed={candidate.key === program.key}
              className={candidate.key === program.key ? "on" : undefined}
              onClick={() => {
                setProgramKey(candidate.key);
                setMonths(null);
              }}
            >
              <strong>{candidate.label}</strong>
              {candidate.note ? <small>{candidate.note}</small> : null}
            </button>
          ))}
        </div>
      ) : null}

      {total == null ? (
        <p className="pdoc-calc-empty">
          Choose an option above and your payment will appear here.
        </p>
      ) : tooSmall ? (
        /* Under the lender's floor there is no loan to illustrate, and a
           figure here would be a payment nobody would write. Jonathon,
           2026-09-22: "grand total estimate must be 2001 or greater." */
        <p className="pdoc-calc-empty">
          Financing is available on projects of {floor} or more.
        </p>
      ) : invalidDown ? (
        <p className="pdoc-calc-empty" role="alert">
          Enter a valid down payment.
        </p>
      ) : tooLarge ? (
        <p className="pdoc-calc-empty">
          Financing is available for loans up to {ceiling}.
        </p>
      ) : overPaid ? (
        <p className="pdoc-calc-empty">
          With that much down there is less than {floor} left to finance.
        </p>
      ) : payment ? (
        <>
          <strong className="pdoc-calc-amt">
            {payment.formatted}
            <small>/month</small>
          </strong>
          <span className="pdoc-calc-terms">
            {formatTerm(chosenMonths)} at {formatApr(program.apr)} APR
          </span>
          {payment.downPayment > 0 ? (
            <span className="pdoc-calc-terms">
              {formatMoney(payment.downPayment)} down,{" "}
              {formatMoney(payment.financed)} financed
            </span>
          ) : null}
        </>
      ) : null}

      {/* Their own down payment, beside the slider: the two things they move.
          Never shown on a job too small to finance at all, where there is
          nothing for either of them to do. */}
      {total != null && !tooSmall ? (
        <label className="pdoc-calc-down">
          <span>
            Down payment
            {/* Jonathon, 2026-09-22: "down payment is not required unless bank
                says so." An empty box beside a payment reads as a blank
                somebody has to fill in, and a customer who thinks they need
                thousands up front stops reading. */}
            <small>Optional — only if the lender asks for one</small>
          </span>
          <input
            type="text"
            inputMode="decimal"
            aria-label="Down payment"
            value={
              downDraft ??
              (calculator.downPayment > 0
                ? String(calculator.downPayment / 100)
                : "")
            }
            placeholder="$0"
            onChange={(e) => setDownDraft(e.target.value)}
          />
        </label>
      ) : null}

      {/* The slider itself, under the figure it moves — Jonathon: "slider can
          be at the bottom once they've made selections". Left out for a
          programme written at one fixed term, where there is nothing to
          choose and a slider that cannot move reads as broken. */}
      {terms.length > 1 &&
      !tooSmall &&
      !invalidDown &&
      !tooLarge &&
      !overPaid &&
      total != null ? (
        <div className="pdoc-calc-slider">
          <input
            type="range"
            min={0}
            max={terms.length - 1}
            step={1}
            value={Math.max(0, terms.indexOf(chosenMonths))}
            aria-label="Length of the loan in months"
            aria-valuetext={formatTerm(chosenMonths)}
            onChange={(e) => setMonths(terms[Number(e.target.value)] ?? null)}
          />
          <div className="pdoc-calc-ends">
            <span>{terms[0]} mo</span>
            <span>{terms[terms.length - 1]} mo</span>
          </div>
        </div>
      ) : null}

      <span className="pdoc-calc-note">{calculator.note}</span>
    </div>
  );
}

/**
 * The colours, as a card per package.
 *
 * A look-book, not a question. Where the manufacturer publishes the lineup the
 * card opens that; where we hold the photographs ourselves — the gutters — the
 * card opens them here, and they stay shut until it is tapped. Left open under
 * the cards, tapping Gutters scrolled the page an inch and looked broken: the
 * chart was already on screen, so nothing appeared to happen.
 */
function ColorsSectionView({
  section,
}: {
  section: Extract<DocumentSection, { id: "colors" }>;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const viewer = section.links.find((link) => link.key === viewing)?.viewer;
  return (
    <Sec section={section} dark heading={twoLine(section.heading)}>
      {/* A card per package, the way the colour sheet Jonathon worked up
              does it: the package on top, what it is made of under it, and one
              link into that chart. A row of bare text links read as a table of
              contents; these read as something to tap. */}
      <div className="pdoc-color-cards">
        {section.links.map((link, index) => {
          /* The manufacturer's page when we hold one, otherwise the chart
                 of photographs further down. A card is never dead. */
          const away = (link.url ?? "").trim() !== "";
          /* An estimate named after its shingle — "GAF Timberline UHDZ -
                 Silver Pledge" — would otherwise print the shingle twice, once
                 as the package and once under it. */
          const product =
            link.product &&
            !link.name.toLowerCase().includes(link.product.toLowerCase())
              ? link.product
              : null;
          const className = `pdoc-color-card tone${index % 4}${
            link.recommended ? " hero" : ""
          }`;
          const inside = (
            <>
              {link.recommended ? (
                <span className="badge">Most popular</span>
              ) : null}
              <span className="head">
                {product ? (
                  <>
                    <span className="tier">{link.name}</span>
                    <span className="name">{product}</span>
                  </>
                ) : (
                  <span className="name">{link.name}</span>
                )}
              </span>
              <span className="cta">
                {open === link.key ? "Hide colors ↑" : "View colors →"}
              </span>
            </>
          );
          if (link.viewer)
            /* A viewer of finished roofs, over the page, and back to the
                   proposal when it closes. */
            return (
              <button
                key={link.key}
                type="button"
                className={className}
                aria-haspopup="dialog"
                onClick={() => setViewing(link.key)}
              >
                {inside}
              </button>
            );
          return away ? (
            <a
              key={link.key}
              className={className}
              href={link.url as string}
              target="_blank"
              rel="noreferrer noopener"
            >
              {inside}
            </a>
          ) : (
            /* Ours to show, so it opens here rather than sending somebody
                   out of the proposal to find their way back. */
            <button
              key={link.key}
              type="button"
              className={className}
              aria-expanded={open === link.key}
              aria-controls={`pdoc-chart-${link.key}`}
              onClick={() =>
                setOpen((was) => (was === link.key ? null : link.key))
              }
            >
              {inside}
            </button>
          );
        })}
      </div>

      {section.shingles
        .filter((chart) => open === chart.key)
        .map((chart) => (
          <div
            key={chart.key}
            className="pdoc-chart"
            id={`pdoc-chart-${chart.key}`}
          >
            <h3>{chart.shingle}</h3>
            <ColorChart
              colors={chart.colors}
              folder="shingle-colors"
              what={`${chart.shingle} roof`}
            />
          </div>
        ))}

      {section.gutters && open === "gutters" ? (
        <div className="pdoc-chart" id="pdoc-chart-gutters">
          <h3>Seamless Gutters</h3>
          <ColorChart
            colors={section.gutters}
            folder="gutter-colors"
            what="seamless gutters"
          />
        </div>
      ) : null}

      {viewer ? (
        <ColorViewer viewer={viewer} onClose={() => setViewing(null)} />
      ) : null}

      {/* The note belongs to a chart, so it waits for one. */}
      {open ? <p className="pdoc-hub-foot">{section.note}</p> : null}
    </Sec>
  );
}

/**
 * One package, as a section of its own.
 *
 * The whole point of the vertical layout: a customer reads one roof at a time,
 * in full, rather than comparing five columns of abbreviations. The price and
 * the Choose button sit at the bottom of each, where somebody who has just
 * finished reading it is looking.
 */
/**
 * The roofs side by side: a column each, the ticks the package already says,
 * its price at the foot. Columns sit in a row on a wide screen and stack on a
 * phone, so nothing scrolls sideways.
 */
function CompareChart({
  section,
  chosen,
}: {
  section: Extract<DocumentSection, { id: "compare" }>;
  chosen: readonly string[];
}) {
  return (
    <Sec section={section} dark heading={twoLine(section.heading)}>
      <div className="pdoc-cmp-grid">
        {section.packages.map((pack, index) => (
          <article
            key={pack.estimateId}
            className={`pdoc-cmp-col tone-${index % 3} ${
              chosen.includes(pack.estimateId) ? "chosen" : ""
            }`}
            aria-label={pack.name}
          >
            {pack.popular ? (
              <span className="pdoc-cmp-badge">Most popular</span>
            ) : null}
            <h3>{pack.name}</h3>
            <ul>
              {pack.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <p className="pdoc-cmp-price">
              {pack.totalFormatted}
              {pack.monthly ? <span className="mo">{pack.monthly}</span> : null}
            </p>
          </article>
        ))}
      </div>
      {section.class4Note ? (
        <p className="pdoc-cmp-note">
          <strong>{CLASS_4_INSURANCE_NOTE.lead}</strong>{" "}
          {CLASS_4_INSURANCE_NOTE.rest}
        </p>
      ) : null}
    </Sec>
  );
}

function PackageSection({
  pack,
  anchorId,
  chosen,
  soleOption,
  chooseMany,
  onChoose,
}: {
  pack: PresentedPackage;
  /** The first package also answers to the section's own anchor. */
  anchorId?: string | undefined;
  chosen: boolean;
  /** The only package on the document: a price, not a choice. */
  soleOption?: boolean;
  /** Several of these can be bought together, so the button adds rather than picks. */
  chooseMany?: boolean;
  onChoose: () => void;
}) {
  return (
    <section
      className={`pdoc-sec pdoc-pkg ${chosen && !soleOption ? "chosen" : ""}`}
      id={anchorId ?? `pdoc-pkg-${pack.estimateId}`}
    >
      <div className="wrap">
        <h2>
          {pack.name}
          {pack.recommended ? (
            <span className="pdoc-mp">Most popular</span>
          ) : null}
        </h2>
        {pack.headline ? <p className="pdoc-kd">{pack.headline}</p> : null}

        {pack.blocks.map((block) => (
          <div key={block.heading}>
            <h3>{block.heading}</h3>
            {block.lines.map((line, index) => (
              <div key={index} className="pdoc-pkg-line">
                {/* A clause under "Please Note" is a paragraph with no bold
                    half, so an empty name prints nothing rather than an empty
                    semibold line above it (ADR 0100). Only documents frozen
                    before 2026-09-10 carry one; since then a clause is part of
                    its line's own body (ADR 0124). */}
                {line.name ? <p className="nm">{line.name}</p> : null}
                {line.body ? <p className="body">{line.body}</p> : null}
              </div>
            ))}
          </div>
        ))}

        <div className="pdoc-subtotal">
          <span>Estimate subtotal</span>
          <span className="pick">
            {/* No button on a document offering one thing. There is nothing to
                choose between, and "Chosen" over a lone package reads as a
                decision somebody has already made for them. */}
            {soleOption ? null : (
              <button
                type="button"
                className={`pdoc-btn ${chosen ? "on" : ""}`}
                aria-pressed={chosen}
                onClick={onChoose}
              >
                {/* On a document where several can be bought, the button is
                    adding this one to the order rather than answering a
                    question — and it has to be able to take it back out, which
                    "Chosen" does not say. */}
                {chooseMany
                  ? chosen
                    ? "Added — tap to remove"
                    : `Add ${pack.name}`
                  : chosen
                    ? "Chosen"
                    : `Choose ${pack.name}`}
              </button>
            )}
            <span className="amt">
              {pack.totalFormatted}
              {pack.monthly ? <span className="mo">{pack.monthly}</span> : null}
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

/**
 * Where they choose, and what it comes to.
 *
 * The radio list is the same choice as the Choose buttons above — one question
 * with one answer, and a screen reader should hear it that way. The totals are
 * computed by the same functions the server uses to price the signature, so
 * what they read here is what they are charged.
 */
function SummarySection({
  section,
  packages,
  upgrades,
  chosen,
  chooseMany,
  onChoose,
  ticked,
  total,
  deposit,
  schedule,
}: {
  section: Extract<DocumentSection, { id: "summary" }>;
  packages: PresentedPackage[];
  upgrades: PresentedUpgrade[];
  chosen: string[];
  chooseMany: boolean;
  onChoose: (estimateId: string) => void;
  ticked: string[];
  total: { total: number; totalFormatted: string } | null;
  deposit: Pick<
    Deposit,
    "required" | "amountFormatted" | "text" | "atSigning"
  > | null;
  schedule: PresentedScheduleRow[] | null;
}) {
  /*
   * The payment on the number they have actually chosen.
   *
   * Worked out here, on their phone, because the total depends on the package
   * and the upgrades they picked minutes after this document was frozen. The
   * terms it uses were frozen with it — `section.financing` — so a document
   * presented before 2026-09-22 has none and falls back to the same $13 per
   * $1,000 it has always shown (ADR 0201).
   */
  const monthly = total
    ? financingPayment(total.total as Cents, section.financing)
    : null;
  /*
   * The upgrades they are actually buying, by name.
   *
   * Jonathon, 2026-08-19: "summary should show the upgrade selected. not just
   * the price." A line reading "2 upgrades added above" asks somebody to
   * scroll back up and count, on the screen where they are about to sign.
   * Included ones are in here too — they are on the roof either way, and
   * saying so is what stops "where are my gutter guards?" after the fact.
   */
  const added = upgrades.filter((upgrade) => ticked.includes(upgrade.key));
  const soleOption = packages.length === 1;
  return (
    <section className="pdoc-sec pdoc-summary" id={SUMMARY_ANCHOR}>
      <div className="wrap">
        <h2>{section.heading}</h2>
        {/* "then sign below" used to point three sections down the page, past
            financing and the colour charts. The Summary now sits directly
            above the agreement, so below is where the signature is — and the
            upgrades were ticked further up, not here, so this no longer asks
            for them a second time. */}
        <p className="pdoc-lead">
          {soleOption
            ? "Here is what it comes to, with anything you added. Sign below to get started."
            : chooseMany
              ? // Not "choose one of these". These are separate pieces of work
                // — a roof and the gutters, a house and a garage — and the
                // whole point is that a customer can have both (ADR 0030).
                "Tick everything you would like us to do. They add up, and you can have as many as you like."
              : section.lead}
        </p>

        <div
          role={soleOption ? undefined : chooseMany ? "group" : "radiogroup"}
          aria-label={
            soleOption
              ? undefined
              : chooseMany
                ? "Choose what you would like"
                : "Choose your package"
          }
        >
          {packages.map((pack) => (
            <div
              key={pack.estimateId}
              // A document offering one package is not asking a question, so it
              // is not a radio and there is nothing to press. Where several can
              // be bought it is a checkbox, because that is what "as many as
              // you like" is — and a screen reader should hear the difference.
              role={soleOption ? undefined : chooseMany ? "checkbox" : "radio"}
              aria-checked={
                soleOption ? undefined : chosen.includes(pack.estimateId)
              }
              tabIndex={soleOption ? undefined : 0}
              className={`pdoc-opt ${soleOption ? "sole" : ""} ${
                !soleOption && chosen.includes(pack.estimateId) ? "on" : ""
              }`}
              onClick={soleOption ? undefined : () => onChoose(pack.estimateId)}
              onKeyDown={
                soleOption
                  ? undefined
                  : (e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        onChoose(pack.estimateId);
                      }
                    }
              }
            >
              {soleOption ? null : (
                <span
                  className={`pdoc-dot ${chooseMany ? "box" : ""} ${
                    chosen.includes(pack.estimateId) ? "on" : ""
                  }`}
                  aria-hidden
                />
              )}
              <span>
                <p className="n">{pack.name}</p>
                {pack.headline ? <p className="k">{pack.headline}</p> : null}
              </span>
              <p className="p">
                {pack.totalFormatted}
                {pack.monthly ? (
                  <span className="mo">{pack.monthly}</span>
                ) : null}
              </p>
            </div>
          ))}
        </div>

        <div className="pdoc-tot">
          <div>
            {added.length > 0 ? (
              <ul className="pdoc-added">
                {added.map((upgrade) => (
                  <li key={upgrade.key}>
                    <span>{upgrade.name}</span>
                    <span>
                      {upgradeIsIncluded(upgrade, chosen)
                        ? "Included"
                        : upgrade.priceFormatted}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <table>
            <tbody>
              <tr className="total">
                <td>Total</td>
                <td>{total ? total.totalFormatted : "—"}</td>
              </tr>
              {section.monthlyNote ? (
                <tr>
                  <td>{section.monthlyNote}</td>
                  <td>{monthly ? `${monthly.formatted}/month` : "—"}</td>
                </tr>
              ) : null}
              {/* What they put down, said once, where the money is added up.
                  It is not deducted from the Total — the Total is what the job
                  costs and what they are signing for — so the only honest
                  place for it is beside the payment it produced. */}
              {monthly && monthly.downPayment > 0 ? (
                <tr>
                  <td>After a down payment of</td>
                  <td>{formatMoney(monthly.downPayment)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {section.deposit ? (
          <div className="pdoc-deposit">
            <b>Deposit due at signing</b>
            <span>
              {deposit
                ? schedule
                  ? deposit.atSigning
                  : deposit.text
                : "Choose a package to see your deposit."}
            </span>
            {/* The rep's own schedule, when they wrote one. Jonathon,
                2026-10-05: "anytime a payment structure is created it needs
                to reflect on the estimate." Every figure comes from
                `presentedSchedule`, which lays the frozen schedule out against
                what they chose; the Final is the remainder, so the rows add
                up to the Total above (ADR 0031, ADR 0267). With it printed,
                the sentence above stops at what is due at signing: "the
                balance is due when the work is complete" would contradict a
                schedule that says otherwise. */}
            {schedule ? (
              <table className="pdoc-schedule" aria-label="Payment schedule">
                <tbody>
                  {schedule.map((row) => (
                    <tr key={row.slot}>
                      <td>{row.label}</td>
                      <td>{row.triggerText}</td>
                      <td>{row.amountFormatted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        ) : null}

        {/* "Read the agreement & sign" was here.
            Jonathon, 2026-08-19: "when i clicked on read the agreement and sign
            it took me back to the dashboard and the estimate went away. that
            button shouldn't be there since the signing is toward the bottom of
            the page anyways." It was an `href="#pdoc-contract"`, and the office
            app routes on the hash — so inside the preview it read as a route
            change. The agreement is the next section down and the Next button
            below goes to it. */}
      </div>
    </section>
  );
}

/**
 * A chart of colours: the names to jump between, then a row of photographs
 * each.
 *
 * Shared by the shingles and the gutters because it is the same problem twice —
 * too many photographs to stack, and a customer who wants to see one colour
 * rather than scroll all of them. Exported for the colour pages a rep sends on
 * their own (`ColorsPage.tsx`), which show the same chart.
 */
export function ColorChart({
  colors,
  folder,
  what,
}: {
  colors: { key: string; name: string; photos: string[] }[];
  folder: string;
  /** What the photographs show, for anybody who cannot see them. */
  what: string;
}) {
  return (
    <>
      <div className="pdoc-color-jump">
        {colors.map((color) => (
          <button
            type="button"
            key={color.key}
            onClick={() => jumpTo(`pdoc-color-${color.key}`)}
          >
            {color.name}
          </button>
        ))}
      </div>

      {colors.map((color) => (
        <div
          key={color.key}
          className="pdoc-color"
          id={`pdoc-color-${color.key}`}
        >
          <h4>{color.name}</h4>
          <div className="pdoc-color-shots">
            {color.photos.map((photo) => {
              const url = colorPhotoUrl(folder, photo);
              return url ? (
                <img
                  key={photo}
                  src={url}
                  alt={`${color.name} ${what}`}
                  loading="lazy"
                />
              ) : null;
            })}
          </div>
        </div>
      ))}
    </>
  );
}
function UpgradeRow({
  upgrade,
  included,
  ticked,
  onTick,
}: {
  upgrade: PresentedUpgrade;
  included: boolean;
  ticked: boolean;
  onTick: () => void;
}) {
  const on = included || ticked;
  return (
    <div className={`pdoc-ucard ${included ? "incl" : ""}`}>
      <div className="t">
        <h4>{upgrade.name}</h4>
        <label className="pdoc-chk">
          <input
            type="checkbox"
            /* Named for what it adds. Four boxes all called "Add" is what a
               screen reader would otherwise read out. */
            aria-label={`Add ${upgrade.name}`}
            checked={on}
            disabled={included}
            onChange={included ? undefined : onTick}
          />
          Add
          {included ? (
            <span className="pdoc-inc-note">— included in your package</span>
          ) : null}
        </label>
      </div>
      {upgrade.blurb ? <p>{upgrade.blurb}</p> : null}
      {/* The manufacturer's own video of it (Jonathon, 2026-10-01). */}
      {upgrade.video ? <UpgradeVideoPlayer video={upgrade.video} /> : null}
      <div className="foot">
        <span>Upgrade subtotal</span>
        <span>
          <b>{included ? "Included" : upgrade.priceFormatted}</b>
          {!included && upgrade.monthly ? (
            <span className="mo">{upgrade.monthly}</span>
          ) : null}
        </span>
      </div>
    </div>
  );
}

/**
 * One section of the document: full width, with the reading column inside it.
 *
 * The packet alternates white sections that ask for a decision with dark ones
 * that carry the brand, so `dark` is the only real variant — and a dark one
 * puts its heading in two lines with the second in copper, which is what makes
 * "Scope / of Work" read the way it does on paper.
 */
function Sec({
  section,
  className,
  dark,
  heading,
  corner = true,
  children,
}: {
  section: DocumentSection;
  className?: string;
  dark?: boolean;
  /** Overrides the frozen heading's line break, never its words. */
  heading?: React.ReactNode;
  /**
   * The mark in the top corner of a dark band.
   *
   * On by default, off on Scope of Work: the cover already carries the logo a
   * few inches above it, and a second one over an eight-step diagram is the
   * company talking about itself in the middle of explaining the roof
   * (Jonathon, 2026-08-19).
   */
  corner?: boolean;
  children: React.ReactNode;
}) {
  const eyebrow = EYEBROW[section.id];
  return (
    <section
      id={sectionAnchor(section.id)}
      className={`${dark ? "pdoc-dark" : "pdoc-sec"} ${className ?? ""}`}
    >
      <div className="wrap">
        {dark && corner ? (
          <img className="corner" src={COMPANY_LOGO_URL} alt="" />
        ) : null}
        {eyebrow ? <p className="pdoc-eyebrow">{eyebrow}</p> : null}
        {dark ? (
          <h2 className="pdoc-h">{heading ?? section.heading}</h2>
        ) : (
          <h2>{section.heading}</h2>
        )}
        {children}
      </div>
    </section>
  );
}

/** "Scope of Work" → "Scope" over "of Work", the second line in copper. */
function twoLine(heading: string): React.ReactNode {
  const words = heading.split(" ");
  if (words.length < 2) return heading;
  // "Scope of Work" breaks after one word, "See Your Color Options" after two:
  // the shorter half leads, which is what puts the weight on the copper line.
  const at = Math.max(1, Math.floor(words.length / 2));
  const first = words.slice(0, at).join(" ");
  const rest = words.slice(at).join(" ");
  return (
    <>
      {first}
      <br />
      <span>{rest}</span>
    </>
  );
}

/**
 * The contract, collapsed.
 *
 * Collapsed by default so the presentation stays a presentation — but the
 * signature button is not reachable until it has been opened, because "I never
 * saw the contract" is exactly the argument this feature exists to settle.
 */
function ContractSectionView({
  section,
  companySignedAt,
  chosen,
  chooseMany,
  chosenUpgrades,
  needsAChoice,
  customerEmail,
  customerPhone,
  onSign,
  signing,
  error,
  preview,
  record,
}: {
  section: Extract<DocumentSection, { id: "contract" }>;
  companySignedAt: string | null;
  /** The estimates they picked. Empty until they pick on a document with a choice. */
  chosen: string[];
  /** Whether they were allowed to pick more than one (ADR 0120). */
  chooseMany: boolean;
  /** Every upgrade on their roof, ticked or included. */
  chosenUpgrades: string[];
  /** Whether this document offered a choice at all. */
  needsAChoice: boolean;
  /** What Roof Board already holds for them, so the form starts filled in. */
  customerEmail: string | null;
  customerPhone: string | null;
  onSign: (signature: SignaturePayload) => void | Promise<void>;
  signing?: boolean | undefined;
  error?: string | null | undefined;
  /** The rep looking at it before it is sent. Nothing here can be signed. */
  preview?: boolean | undefined;
  /** The office reading it afterwards. The signature replaces the form. */
  record?:
    | {
        signature: DocumentSignature | null;
        initials?: Record<string, unknown>;
      }
    | null
    | undefined;
}) {
  // The agreement opens itself on a record: somebody reading one has come to
  // read the terms, and asking them to press "Read the agreement" first would
  // be a screen hiding the thing it exists to show.
  const [open, setOpen] = useState(Boolean(record));
  const [initials, setInitials] = useState<Record<string, string>>({});
  const [consented, setConsented] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState(customerEmail ?? "");
  const [signerPhone, setSignerPhone] = useState(customerPhone ?? "");

  /*
   * The date beside the customer's signature.
   *
   * Today by default, and theirs to change — the same field they would fill in
   * by hand. `useState` with an initialiser rather than a constant so it is
   * read once, when the page opens, instead of drifting if somebody leaves the
   * document up past midnight. See packages/core/src/signedDate.ts for why it
   * is bounded and why the server keeps its own timestamp regardless.
   */
  const [today] = useState(() => localDateString(new Date()));
  const [signedDate, setSignedDate] = useState(today);
  const dateWindow = signedDateWindow(today);
  const dateIsGood = signedDateIsValid(signedDate, today);

  /*
   * Our half of the signature block.
   *
   * A document frozen before 2026-08-16 carries neither a signer nor a date of
   * its own (ADR 0032); it had one line of prose reading off the proposal row.
   * Falling back to today's signer name would print somebody onto a contract
   * they did not sign, so a document without one gets the old line instead.
   */
  const companySigner = section.companySigner ?? null;
  const companyDate =
    section.companySignedDate ?? companySignedAt?.slice(0, 10) ?? null;

  /*
   * The boxes this document actually draws, not the ones the contract asks for
   * today.
   *
   * A presented document is frozen (ADR 0032), so one sent before the contract
   * dropped from six initial boxes to two still renders all six. Validating it
   * against the current two would light up the Sign button with four boxes on
   * screen still empty, and record an acceptance that does not match the paper
   * the customer is reading. So the requirement is read off the snapshot.
   */
  const required = section.blocks.flatMap((block) =>
    block.kind === "initial" ? [block.key] : [],
  );

  // A packet is not signable until a package has been picked: the acceptance
  // records which estimate was bought, and there is no sensible one to record
  // for somebody who never chose.
  const unchosen = needsAChoice && chosen.length === 0;
  const complete =
    !unchosen &&
    dateIsGood &&
    signatureIsComplete({ initials, consented, signerName }, required);

  /*
   * What they still have to do, and the one to point at.
   *
   * Jonathon, 2026-09-07: "when the customer is going through the agreement to
   * esign it needs to highlight what they need to do next, like click on read
   * the agreement, check the box, put your name in, sign this agreement." A
   * greyed-out Sign button and one line of small print told somebody who
   * already knew the form; it did not tell a homeowner on a phone how far off
   * they were.
   *
   * The list is not a second opinion about whether this can be signed — the
   * button still reads `signatureIsComplete`, and the Edge Function checks
   * again server-side. See packages/core/src/signingSteps.ts.
   *
   * A record shows what somebody did rather than asking anything, so it gets
   * no list. A rep's preview does: it is the customer's screen, and being able
   * to see it before it goes is the point of the preview.
   */
  const stepInput = {
    needsAChoice,
    chosen,
    chooseMany,
    agreementOpen: open,
    requiredInitials: required,
    initials,
    consented,
    signerName,
  };
  const steps = record ? [] : signingSteps(stepInput);
  const now = record ? null : nextSigningStep(stepInput).id;
  /** The ring on the control the current step is asking for. */
  const focus = (step: string) => (now === step ? " pdoc-focus" : "");

  return (
    <section className="pdoc-sec pdoc-terms" id={CONTRACT_ANCHOR}>
      <div className="wrap">
        <h2>{section.heading}</h2>

        {steps.length > 0 ? (
          <ol className="pdoc-steps" aria-label="What to do to sign">
            {steps.map((step) => (
              <li
                key={step.id}
                className={`${step.done ? "done" : ""}${
                  step.id === now ? " now" : ""
                }`}
                /* The step they are on is the one thing on this screen a
                   customer must not miss, so it is announced as well as
                   coloured — a ring is nothing to somebody using a screen
                   reader, and this is the page where being stuck is expensive. */
                aria-current={step.id === now ? "step" : undefined}
              >
                {step.id === "choose" && !step.done ? (
                  <button
                    type="button"
                    className="pdoc-inline-link"
                    onClick={() => jumpTo(SUMMARY_ANCHOR)}
                  >
                    {step.label}
                  </button>
                ) : (
                  step.label
                )}
              </li>
            ))}
          </ol>
        ) : null}

        <button
          type="button"
          className={`pdoc-toggle${focus("read")}`}
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
        >
          {open ? "Hide the agreement" : "Read the agreement"}
        </button>

        {open ? (
          <div className="pdoc-legal">
            {section.blocks.map((block, index) => (
              <Block
                key={index}
                block={block}
                initialled={
                  block.kind === "initial"
                    ? Boolean(
                        record
                          ? record.initials?.[block.key]
                          : initials[block.key],
                      )
                    : false
                }
                /* On a record the box reports what the customer did. Tapping
                   one would be the office initialling a contract on their
                   behalf, so it does nothing. */
                onInitial={(key) =>
                  record
                    ? undefined
                    : setInitials((was) => ({
                        ...was,
                        [key]: new Date().toISOString(),
                      }))
                }
              />
            ))}
          </div>
        ) : null}

        {record ? (
          <SignatureRecord
            signature={record.signature}
            companySigner={companySigner}
            companyDate={companyDate}
            role={section.kind === "repair" ? "Contractor" : "Company"}
          />
        ) : (
          <div className="pdoc-sign">
            <label className={`pdoc-consent${focus("consent")}`}>
              <input
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
              />
              <span>
                I agree to do business electronically and to sign this agreement
                with an electronic signature.
              </span>
            </label>

            {/* The two parties, the way the paper contract lays them out: ours
              already signed and dated, theirs to fill in. Typing a name into the
              box below is the signature, so it is shown back in the same hand as
              the company's — a customer should be able to see what they signed,
              not just that they typed. */}
            {companySigner ? (
              <div className="pdoc-parties">
                <div className="pdoc-party">
                  <span className="pdoc-party-role">
                    {section.kind === "repair" ? "Contractor" : "Company"}
                  </span>
                  <span className="pdoc-hand">{companySigner}</span>
                  <span className="pdoc-party-date">
                    Date: {companyDate ?? "—"}
                  </span>
                </div>
                <div className="pdoc-party">
                  <span className="pdoc-party-role">Customer</span>
                  <span className="pdoc-hand pdoc-hand-theirs">
                    {signerName.trim()}
                  </span>
                  <span className="pdoc-party-date">Date: {signedDate}</span>
                </div>
              </div>
            ) : null}

            <label className={focus("name").trim()}>
              Your full name
              <input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Type your name to sign"
                autoComplete="name"
              />
            </label>

            <label>
              Date
              <input
                type="date"
                value={signedDate}
                min={dateWindow.min}
                max={dateWindow.max}
                onChange={(e) => setSignedDate(e.target.value)}
              />
            </label>

            <div className="pdoc-two">
              <label>
                Email
                <input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  autoComplete="email"
                />
              </label>
              <label>
                Phone
                <input
                  type="tel"
                  value={signerPhone}
                  onChange={(e) => setSignerPhone(e.target.value)}
                  autoComplete="tel"
                />
              </label>
            </div>

            {/* The package, the agreement and the initial boxes used to say
                their piece here, one line at a time. The step list above says
                all of it at once, and in the order it has to happen, so saying
                it twice would only be two places to keep in step.

                The date stays, because it is not a step: it fills itself in
                with today and is right unless somebody changes it, so it is a
                correction rather than something to go and do. The initial
                boxes still gate the button: `signatureIsComplete` reads them,
                as it always did. */}
            {!dateIsGood ? (
              <p className="pdoc-note">
                Please check the date — it should be the day you are signing.
              </p>
            ) : null}

            {error ? (
              <p className="err" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="button"
              className={`pdoc-primary${focus("sign")}`}
              disabled={preview || !open || !complete || signing}
              onClick={() =>
                void onSign({
                  signerName: signerName.trim(),
                  signerEmail: signerEmail.trim() || null,
                  signerPhone: signerPhone.trim() || null,
                  consented,
                  initials,
                  signedDate,
                  // The old field only when it is the whole answer. A server
                  // from before ADR 0120 reads nothing else, and handed the
                  // first of two it would approve the roof alone at the roof's
                  // price while the customer agreed to both. Null makes that
                  // server refuse with "please choose" instead of signing half
                  // a contract.
                  chosenEstimateId: chosen.length === 1 ? chosen[0]! : null,
                  chosenEstimateIds: chosen,
                  chosenUpgrades,
                })
              }
            >
              {signing ? "Signing…" : "Sign this agreement"}
            </button>

            {/* The old one-line version, for a document frozen before the company
              signature block existed. It has no signer name on it, so this is
              all there is to say about our half of it. */}
            {companySigner ? null : (
              <p className="pdoc-company">
                Signed for the company on {companyDate ?? "—"}.
              </p>
            )}

            {/* "I do not want to go ahead" was here.
              Jonathon, 2026-08-13: 'there shouldn't be a "i don't want to go
              ahead"'. A customer who is not ready simply closes the page — the
              proposal is still open and the rep still calls them. A button that
              asks somebody to declare they are out, on the screen where they were
              about to sign, only ever makes that outcome easier to reach. */}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * The signature, where the signing form is on the customer's copy.
 *
 * Drawn in the same two-party shape the form draws, so the office is looking at
 * the same block the customer signed rather than a table of database columns —
 * and then the audit trail underneath it, which is the part a customer never
 * sees and the part that matters if the agreement is ever argued about.
 *
 * A document nobody has signed says so in one line. It is not an error and not
 * an empty state: a sent document with no signature on it is the ordinary case
 * for anything the customer has not got to yet.
 */
function SignatureRecord({
  signature,
  companySigner,
  companyDate,
  role,
}: {
  signature: DocumentSignature | null;
  companySigner: string | null;
  companyDate: string | null;
  role: string;
}) {
  if (!signature) {
    return (
      <div className="pdoc-record">
        <p className="pdoc-record-none">
          Nobody has signed this document yet.
          {companySigner
            ? ` Signed for the company by ${companySigner}${
                companyDate ? ` on ${companyDate}` : ""
              }.`
            : ""}
        </p>
      </div>
    );
  }

  return (
    <div className="pdoc-record">
      <div className="pdoc-parties">
        <div className="pdoc-party">
          <span className="pdoc-party-role">{role}</span>
          <span className="pdoc-hand">{companySigner ?? "—"}</span>
          <span className="pdoc-party-date">Date: {companyDate ?? "—"}</span>
        </div>
        <div className="pdoc-party">
          <span className="pdoc-party-role">Customer</span>
          <span className="pdoc-hand pdoc-hand-theirs">
            {signature.signerName}
          </span>
          <span className="pdoc-party-date">
            Date: {signature.signedDate ?? "—"}
          </span>
        </div>
      </div>

      <dl className="pdoc-audit">
        <Fact label="Signed electronically" value={signature.acceptedAt} />
        {signature.signerEmail ? (
          <Fact label="Email" value={signature.signerEmail} />
        ) : null}
        {signature.signerPhone ? (
          <Fact label="Phone" value={signature.signerPhone} />
        ) : null}
        {signature.ip ? <Fact label="From" value={signature.ip} /> : null}
        {signature.userAgent ? (
          <Fact label="On" value={signature.userAgent} />
        ) : null}
        {signature.customerNote ? (
          <Fact label="Their note" value={signature.customerNote} />
        ) : null}
      </dl>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

function Block({
  block,
  initialled,
  onInitial,
}: {
  block: ContractBlock;
  initialled: boolean;
  onInitial: (key: string) => void;
}) {
  if (block.kind === "heading") return <h3>{block.text}</h3>;
  if (block.kind === "paragraph") return <p>{block.text}</p>;

  return (
    <div className={`pdoc-initial ${initialled ? "done" : ""}`}>
      <button
        type="button"
        aria-label={`Initial: ${block.key}`}
        aria-pressed={initialled}
        onClick={() => onInitial(block.key)}
      >
        {initialled ? "✓" : "Tap to initial"}
      </button>
      <p>{block.text}</p>
    </div>
  );
}
